<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import Monitor from "@/services/Monitor";
import Customers from "@/services/Customers";
import modalManageProfileDefinitions from "@/components/modals/app/modalManageProfileDefinitions";
import Swal from "sweetalert2";

function isPlainObject(value) {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

// A profile fields config is an object whose values are context maps,
// and each context map's values are field config objects. This is shape-based
// so new context names work without code changes.
function isProfileFieldsConfig(payload) {
  if (!isPlainObject(payload)) {
    return false;
  }
  const contexts = Object.keys(payload);
  if (contexts.length === 0) {
    return false;
  }
  return contexts.every((context) => isPlainObject(payload[context]));
}

export default {
  components: { Layout, PageHeader, modalManageProfileDefinitions },
  page: {
    title: "Profile Fields",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  data() {
    return {
      title: "Profile Fields",
      items: [
        { text: "CRM" },
        { text: "Profile Fields", active: true },
      ],
      isMonitorMode: process.env.VUE_APP_TENANT === "MON",
      tenants: [],
      currentTenant:
        process.env.VUE_APP_TENANT === "MON"
          ? ""
          : process.env.VUE_APP_TENANT || "",
      apps: [],
      selectedAppKey: "",
      contextDefinitions: [],
      fieldDefinitions: [],
      activeContext: "",
      isBusy: false,
      isDefinitionsLoading: false,
    };
  },
  computed: {
    contexts() {
      return this.contextDefinitions
        .filter((context) => context.active)
        .map((context) => context.name);
    },
    activeContextDefinition() {
      return (
        this.contextDefinitions.find(
          (context) => context.name === this.activeContext
        ) || null
      );
    },
    contextLabel() {
      return this.activeContext ? this.humanizeField(this.activeContext) : "";
    },
    activeContextProperties() {
      return this.activeContextDefinition?.properties || [];
    },
    activeFieldDefinitions() {
      return this.fieldDefinitions.filter((field) => field.active);
    },
    tableFields() {
      const fields = [{ key: "label", label: "Field", sortable: true }];
      this.activeContextProperties.forEach((property) => {
        fields.push({ key: property, label: this.humanizeField(property) });
      });
      return fields;
    },
    selectedApp() {
      if (!this.apps.length) {
        return null;
      }
      return (
        this.apps.find((app) => this.getAppKey(app) === this.selectedAppKey) ||
        this.apps[0]
      );
    },
    selectedAppFieldRows() {
      return this.selectedApp ? this.getFieldRows(this.selectedApp) : [];
    },
  },
  created() {
    this.loadDefinitions();

    if (this.isMonitorMode) {
      this.loadTenants();
    } else if (this.currentTenant) {
      this.loadProfileFields();
    }
  },
  methods: {
    openManageDefinitions() {
      this.$bvModal.show("manage_profile_definitions");
    },

    loadDefinitions() {
      this.isDefinitionsLoading = true;

      const api = this.isMonitorMode ? Monitor : Customers;

      Promise.all([
        api.getProfileContextDefinitions("all"),
        api.getProfileFieldsDefinitions("all"),
      ])
        .then(([contextResponse, fieldResponse]) => {
          this.contextDefinitions = contextResponse.data.data || [];
          this.fieldDefinitions = fieldResponse.data.data || [];
          this.ensureValidActiveContext();
        })
        .catch((error) => {
          this.contextDefinitions = [];
          this.fieldDefinitions = [];
          this.failedmsg(
            "Failed to load definitions",
            error.response?.data?.error || error.message
          );
        })
        .finally(() => {
          this.isDefinitionsLoading = false;
        });
    },

    parseAppsFromResponse(response) {
      const body = response?.data;

      // Monitor GET: { data: [ { app_id, app_name, profile_fields_config } ] }
      if (Array.isArray(body?.data)) {
        return body.data.map((app) => this.wrapApp(app));
      }

      // Club GET: { registration, profile_update } at root
      if (isProfileFieldsConfig(body)) {
        return [
          this.wrapApp({
            app_id: null,
            app_name: process.env.VUE_APP_TENANT || "Application",
            profile_fields_config: body,
          }),
        ];
      }

      return [];
    },

    extractConfigFromSaveResponse(response) {
      const body = response?.data;

      // Monitor PATCH: { data: { registration, profile_update } }
      if (isProfileFieldsConfig(body?.data)) {
        return body.data;
      }

      // Club PATCH: { registration, profile_update } at root
      if (isProfileFieldsConfig(body)) {
        return body;
      }

      return null;
    },

    loadTenants() {
      Monitor.getTenants()
        .then((response) => {
          const tenants = response.data.tenants || [];
          this.tenants = tenants.filter((tenant) => tenant !== "COMMON");
        })
        .catch(() => {
          this.tenants = [];
        });
    },

    loadProfileFields() {
      if (this.isMonitorMode && !this.currentTenant) {
        return;
      }

      this.isBusy = true;
      this.apps = [];

      const request = this.isMonitorMode
        ? Monitor.getTenantProfileFieldsConfig(this.currentTenant)
        : Customers.getProfileFieldsConfig();

      request
        .then((response) => {
          this.apps = this.parseAppsFromResponse(response);
          this.ensureSelectedApp();
          this.ensureValidActiveContext();
        })
        .catch((error) => {
          this.failedmsg(
            "Load failed",
            error.response?.data?.error || error.message
          );
          this.apps = [];
        })
        .finally(() => {
          this.isBusy = false;
        });
    },

    wrapApp(app) {
      return {
        ...app,
        profile_fields_config: JSON.parse(
          JSON.stringify(app.profile_fields_config || {})
        ),
        _snapshot: JSON.stringify(app.profile_fields_config || {}),
        saving: false,
      };
    },

    getAppKey(app) {
      return app.app_id != null ? String(app.app_id) : app.app_name;
    },

    ensureSelectedApp() {
      if (!this.apps.length) {
        this.selectedAppKey = "";
        return;
      }

      const keys = this.apps.map((app) => this.getAppKey(app));
      if (!keys.includes(this.selectedAppKey)) {
        this.selectedAppKey = keys[0];
      }
    },

    selectApp(app) {
      this.selectedAppKey = this.getAppKey(app);
    },

    isDirty(app) {
      return (
        JSON.stringify(app.profile_fields_config || {}) !== app._snapshot
      );
    },

    ensureValidActiveContext() {
      if (!this.contexts.includes(this.activeContext)) {
        this.activeContext = this.contexts[0] || "";
      }
    },

    getFieldRows(app) {
      const contextConfig = app.profile_fields_config?.[this.activeContext] || {};

      return this.activeFieldDefinitions.map((fieldDefinition) => {
        const field = fieldDefinition.name;
        const existingConfig = contextConfig[field] || {};

        const config = {};
        this.activeContextProperties.forEach((property) => {
          config[property] = !!existingConfig[property];
        });

        return {
          field,
          label: this.humanizeField(field),
          config,
        };
      });
    },

    humanizeField(field) {
      return field
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
    },

    updateFieldFlag(app, field, property, value) {
      if (!app.profile_fields_config[this.activeContext]) {
        this.$set(app.profile_fields_config, this.activeContext, {});
      }
      if (!app.profile_fields_config[this.activeContext][field]) {
        const defaultConfig = {};
        this.activeContextProperties.forEach((prop) => {
          defaultConfig[prop] = false;
        });
        this.$set(
          app.profile_fields_config[this.activeContext],
          field,
          defaultConfig
        );
      }
      this.$set(
        app.profile_fields_config[this.activeContext][field],
        property,
        value
      );
    },

    saveApp(app) {
      if (!this.isDirty(app)) {
        return;
      }

      Swal.fire({
        title: "Save profile fields?",
        text: `Update configuration for ${app.app_name}?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#34c38f",
        cancelButtonColor: "#f46a6a",
        confirmButtonText: "Save",
      }).then((result) => {
        if (!result.value) {
          return;
        }

        app.saving = true;

        const payload = {
          profile_fields_config: app.profile_fields_config,
        };

        const request = this.isMonitorMode
          ? Monitor.updateTenantProfileFieldsConfig({
              tenant: this.currentTenant,
              app_id: app.app_id,
              ...payload,
            })
          : Customers.updateProfileFieldsConfig(payload);

        request
          .then((response) => {
            const updatedConfig =
              this.extractConfigFromSaveResponse(response) ||
              app.profile_fields_config;

            app.profile_fields_config = JSON.parse(
              JSON.stringify(updatedConfig)
            );
            app._snapshot = JSON.stringify(app.profile_fields_config);
            this.successmsg("Profile fields saved successfully");
          })
          .catch((error) => {
            this.failedmsg(
              "Save failed",
              error.response?.data?.error || error.message
            );
          })
          .finally(() => {
            app.saving = false;
          });
      });
    },

    resetApp(app) {
      app.profile_fields_config = JSON.parse(app._snapshot || "{}");
    },

    successmsg(message) {
      Swal.fire({
        title: "Success",
        text: message,
        icon: "success",
        confirmButtonColor: "#34c38f",
      });
    },

    failedmsg(title, message) {
      const text = Array.isArray(message) ? message.join(", ") : message;
      Swal.fire({
        title: title || "Error",
        text: text || "Something went wrong",
        icon: "error",
        confirmButtonColor: "#f46a6a",
      });
    },
  },
  middleware: "authentication",
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row">
      <div class="col-12">
        <div class="card mb-3">
          <div class="card-body">
            <div class="row align-items-end">
              <!-- Left: Tenant + Load -->
              <div class="col-lg-6 border-end-lg">
                <div class="row align-items-end">
                  <div v-if="isMonitorMode" class="col-md-7 mb-3 mb-lg-0">
                    <label class="form-label">Select Tenant</label>
                    <select v-model="currentTenant" class="form-select">
                      <option value="">Select tenant</option>
                      <option
                        v-for="tenant in tenants"
                        :key="tenant"
                        :value="tenant"
                      >
                        {{ tenant }}
                      </option>
                    </select>
                  </div>

                  <div
                    class="mb-3 mb-lg-0"
                    :class="isMonitorMode ? 'col-md-2' : 'col-md-12'"
                  >
                    <button
                      type="button"
                      class="btn btn-primary waves-effect waves-light w-100"
                      :disabled="isMonitorMode && !currentTenant"
                      @click="loadProfileFields"
                    >
                      <i class="mdi mdi-refresh me-1"></i>
                      Load
                    </button>
                  </div>
                </div>
              </div>

              <!-- Right: Context + Manage -->
              <div class="col-lg-6">
                <div class="row align-items-end">
                  <div class="col-md-3 mb-3 mb-lg-0">
                    <label class="form-label">Context</label>
                    <select
                      v-model="activeContext"
                      class="form-select"
                      :disabled="isDefinitionsLoading || contexts.length === 0"
                    >
                      <option v-if="contexts.length === 0" value="">
                        No contexts
                      </option>
                      <option
                        v-for="context in contexts"
                        :key="context"
                        :value="context"
                      >
                        {{ humanizeField(context) }}
                      </option>
                    </select>
                  </div>

                  <div class="col-md-5 mb-3 mb-lg-0">
                    <button
                      type="button"
                      class="btn btn-outline-primary waves-effect waves-light w-100"
                      @click="openManageDefinitions"
                    >
                      <i class="mdi mdi-cog-outline me-1"></i>
                      Manage Definitions and Fields
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="isBusy" class="text-center my-4">
          <b-spinner variant="primary" class="align-middle"></b-spinner>
          <strong class="ms-2">Loading...</strong>
        </div>

        <div v-else-if="!selectedApp" class="card">
          <div class="card-body text-center text-muted py-5">
            No profile field configuration found.
          </div>
        </div>

        <div v-else class="card profile-fields-app-card">
          <div class="card-body">
            <div v-if="apps.length > 1" class="text-center mb-3">
              <label class="form-label d-block fw-bold">Application</label>
              <div class="btn-group flex-wrap" role="group">
                <button
                  v-for="app in apps"
                  :key="getAppKey(app)"
                  type="button"
                  class="btn"
                  :class="
                    getAppKey(app) === selectedAppKey
                      ? 'btn-primary'
                      : 'btn-light'
                  "
                  @click="selectApp(app)"
                >
                  {{ app.app_name }}
                </button>
              </div>
            </div>

            <div
              class="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2"
            >
              <div class="d-flex flex-wrap align-items-center gap-2">
                <h5 class="mb-0">{{ selectedApp.app_name }}</h5>

                <div class="d-flex flex-wrap gap-2">
                  <span
                    v-if="selectedApp.app_type"
                    class="badge bg-secondary font-size-12"
                    >{{ selectedApp.app_type }}</span
                  >
                  <span
                    v-if="selectedApp.role_source"
                    class="badge bg-info font-size-12"
                    >{{ selectedApp.role_source }}</span
                  >
                  <span
                    v-if="selectedApp.api_key"
                    class="badge bg-light text-dark font-size-12"
                    >{{ selectedApp.api_key }}</span
                  >
                </div>
              </div>

              <div class="d-flex gap-2">
                <button
                  type="button"
                  class="btn btn-light"
                  :disabled="!isDirty(selectedApp) || selectedApp.saving"
                  @click="resetApp(selectedApp)"
                >
                  Reset
                </button>
                <button
                  type="button"
                  class="btn btn-success"
                  :disabled="!isDirty(selectedApp) || selectedApp.saving"
                  @click="saveApp(selectedApp)"
                >
                  <b-spinner
                    v-if="selectedApp.saving"
                    small
                    class="me-1"
                  ></b-spinner>
                  Save
                </button>
              </div>
            </div>

            <p class="text-muted mb-3">
              Context: <strong>{{ contextLabel }}</strong>
            </p>

            <div class="table-responsive">
              <b-table
                :items="selectedAppFieldRows"
                :fields="tableFields"
                primary-key="field"
                responsive="sm"
                small
                show-empty
                empty-text="No fields configured"
              >
                <template #cell(label)="data">
                  {{ data.item.label }}
                </template>

                <template #cell()="data">
                  <b-form-checkbox
                    v-if="activeContextProperties.includes(data.field.key)"
                    :key="`${selectedAppKey}-${activeContext}-${data.item.field}-${data.field.key}`"
                    :checked="!!data.item.config[data.field.key]"
                    switch
                    @change="
                      updateFieldFlag(
                        selectedApp,
                        data.item.field,
                        data.field.key,
                        $event
                      )
                    "
                  />
                  <span v-else class="text-muted">—</span>
                </template>
              </b-table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <modalManageProfileDefinitions
      :is-monitor-mode="isMonitorMode"
      @refresh="loadDefinitions"
    />
  </Layout>
</template>

<style scoped>
@media (min-width: 992px) {
  .border-end-lg {
    border-right: 1px solid var(--bs-border-color, #dee2e6);
  }
}
</style>
